/**
 * One recorded step's screenshot: the visible page, with the step's element outlined.
 *
 * The same approach as "Report a problem" (`helpdesk/support-context/capture-screenshot.ts`):
 * html-to-image renders the viewport in the browser, and password fields and anything
 * marked `data-support-redact` are left out. GMS's own chrome — the recording banner,
 * the drawer, the overlay — carries `data-gms-ui` and is left out too, so no screenshot
 * shows the recorder recording itself.
 *
 * Recording happens on live data (G20), so these images can show real details. The
 * author blurs them in Review & Edit, and approval requires confirming that every one
 * was checked.
 *
 * **Freeze, then render.** html-to-image copies the page asynchronously, so rendering
 * the live page from a click handler photographs whatever the click did — a menu half
 * open, "Loading…", the next screen. `captureStep` therefore copies the page
 * *synchronously* when it is called (the recorder calls it from capture-phase
 * `pointerdown` / `focusin`, before the page's own handlers run), including what the
 * DOM does not clone: typed values, chosen options, ticks and canvases. The copy is
 * rendered later, off-screen, and the page can change freely in between.
 */

import { GMS_UI_ATTR } from './locator';

/** The server's limit on one screenshot (`gms/services/screenshots.py`). */
const MAX_BYTES = 2 * 1024 * 1024;
const MAX_PIXEL_RATIO = 1.5;
const CAPTURE_TIMEOUT_MS = 10_000;
const HIGHLIGHT_ATTR = 'data-gms-recording-target';
/** Marks the off-screen copy, so nothing (the recorder included) treats it as the page. */
export const FROZEN_ATTR = 'data-gms-frozen-frame';

/** 1×1 transparent PNG used when a (cross-origin) image cannot be inlined. */
const IMAGE_PLACEHOLDER =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

function includeNode(node: HTMLElement): boolean {
  if (!(node instanceof Element)) return true;
  if (node.matches('input[type="password" i]')) return false;
  if (node.hasAttribute('data-support-redact')) return false;
  if (node.hasAttribute(GMS_UI_ATTR)) return false;
  return true;
}

let highlightStyle: HTMLStyleElement | null = null;

/** The outline drawn on the step's element. Inserted once, scoped by an attribute. */
function ensureHighlightStyle(): void {
  if (highlightStyle?.isConnected) return;
  highlightStyle = document.createElement('style');
  // Scrollbars hidden inside the copy only: the off-screen host shows them where the
  // real page does not, and they are noise in a guide.
  highlightStyle.textContent = [
    `[${HIGHLIGHT_ATTR}] { outline: 3px solid #2563eb !important; outline-offset: 3px !important; }`,
    `[${FROZEN_ATTR}], [${FROZEN_ATTR}] * { scrollbar-width: none !important; }`,
    `[${FROZEN_ATTR}] ::-webkit-scrollbar { display: none !important; }`,
  ].join('\n');
  document.head.appendChild(highlightStyle);
}

/**
 * A synchronous copy of the visible page, with `element`'s counterpart outlined.
 *
 * Returned detached; `renderFrame` attaches it off-screen only while rendering. The
 * host establishes a containing block (its transform), so the copy's fixed header and
 * sidebar stay inside it instead of painting over the real page.
 */
/** How the step's own field should look in its picture: as it was before the author acted. */
export interface FieldBefore {
  /** For a fill step: show the field empty, never what was typed. */
  blank?: boolean;
  /** For a select step: the option chosen before the change (-1 for none). */
  selectedIndex?: number;
}

function freezeFrame(element: Element | null, before?: FieldBefore): HTMLElement {
  const host = document.createElement('div');
  host.setAttribute(FROZEN_ATTR, '');
  host.setAttribute('aria-hidden', 'true');
  host.className = document.body.className;
  const body = getComputedStyle(document.body);
  Object.assign(host.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: `${window.innerWidth}px`,
    height: `${window.innerHeight}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
    transform: 'translateX(-200vw)',
    background: body.backgroundColor,
    color: body.color,
    font: body.font,
  });

  const sources = Array.from(document.body.childNodes);
  for (const node of sources) host.appendChild(node.cloneNode(true));

  // cloneNode copies attributes, not live state. Same query, same order, both trees.
  const live = document.body.querySelectorAll<HTMLElement>(LIVE_STATE);
  const copied = host.querySelectorAll<HTMLElement>(LIVE_STATE);
  live.forEach((source, index) => copyLiveState(source, copied[index]));

  if (element) {
    const counterpart = counterpartOf(element, host);
    counterpart?.setAttribute(HIGHLIGHT_ATTR, '');
    // A fill or select step is captured on its change, so the live field already holds
    // the new value; the copy is put back to how it looked before.
    if (before?.blank && (counterpart instanceof HTMLInputElement || counterpart instanceof HTMLTextAreaElement)) {
      counterpart.value = '';
    }
    if (before?.selectedIndex !== undefined && counterpart instanceof HTMLSelectElement) {
      counterpart.selectedIndex = before.selectedIndex;
      Array.from(counterpart.options).forEach((option, index) => {
        option.toggleAttribute('selected', index === before.selectedIndex);
      });
    }
  }
  // The copy is attached while it renders, so anything that searches the page could
  // find it. Once the index-based matching above is done, strip what must never be
  // found or photographed: an earlier step's copy, GMS's own UI (the banner, drawer and
  // overlay), password fields and redacted areas — and every id and target attribute,
  // so a lookup by id, data-gms-target or data-testid can only reach the real page.
  // (No stylesheet in src/ selects by id, so dropping ids does not change the picture.)
  host
    .querySelectorAll(
      `[${FROZEN_ATTR}], [${GMS_UI_ATTR}], input[type="password" i], [data-support-redact]`
    )
    .forEach((excluded) => excluded.remove());
  host.querySelectorAll('[id], [data-gms-target], [data-testid]').forEach((node) => {
    node.removeAttribute('id');
    node.removeAttribute('data-gms-target');
    node.removeAttribute('data-testid');
  });
  host.setAttribute('inert', '');
  // A lazy image in an off-screen copy would never load; html-to-image reads `src`.
  host.querySelectorAll('img[loading="lazy"]').forEach((image) => image.setAttribute('loading', 'eager'));
  host.querySelectorAll('video').forEach((video) => video.removeAttribute('autoplay'));
  return host;
}

const LIVE_STATE = 'input, textarea, select, canvas';

function copyLiveState(source: HTMLElement, copy: HTMLElement | undefined): void {
  if (!copy || copy.tagName !== source.tagName) return;
  if (source instanceof HTMLInputElement && copy instanceof HTMLInputElement) {
    copy.value = source.value;
    copy.checked = source.checked;
  } else if (source instanceof HTMLTextAreaElement && copy instanceof HTMLTextAreaElement) {
    copy.value = source.value;
  } else if (source instanceof HTMLSelectElement && copy instanceof HTMLSelectElement) {
    copy.selectedIndex = source.selectedIndex;
    Array.from(copy.options).forEach((option, index) => {
      option.toggleAttribute('selected', index === source.selectedIndex);
    });
  } else if (source instanceof HTMLCanvasElement && copy instanceof HTMLCanvasElement) {
    try {
      copy.getContext('2d')?.drawImage(source, 0, 0);
    } catch {
      // A tainted canvas stays blank in the picture rather than failing the step.
    }
  }
}

/** The node at the same child-index path under `root` as `element` is under body. */
function counterpartOf(element: Element, root: Element): Element | null {
  const path: number[] = [];
  let node: Node | null = element;
  while (node && node !== document.body) {
    const parent: Node | null = node.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, node));
    node = parent;
  }
  if (node !== document.body) return null;
  let current: Node | undefined = root;
  for (const index of path) current = current?.childNodes[index];
  return current instanceof Element ? current : null;
}

async function renderFrame(frame: HTMLElement, pixelRatio: number): Promise<Blob | null> {
  // Loaded on first use so the library stays out of the main bundle.
  const { toBlob } = await import('html-to-image');
  const capture = toBlob(frame, {
    pixelRatio,
    width: window.innerWidth,
    height: window.innerHeight,
    canvasWidth: window.innerWidth,
    canvasHeight: window.innerHeight,
    filter: includeNode,
    imagePlaceholder: IMAGE_PLACEHOLDER,
    cacheBust: false,
    // The host sits off-screen; the picture should not.
    style: { margin: '0', transform: 'none' },
  });
  let timer = 0;
  const timeout = new Promise<null>((_, reject) => {
    timer = window.setTimeout(() => reject(new Error('Screenshot timed out')), CAPTURE_TIMEOUT_MS);
  });
  try {
    return await Promise.race([capture, timeout]);
  } finally {
    window.clearTimeout(timer);
  }
}

// One capture at a time: html-to-image clones the whole page, and two at once would
// each slow the other and the page underneath.
let queue: Promise<unknown> = Promise.resolve();
/** Copies taken and not yet rendered. */
let pending = 0;
const MAX_PENDING = 4;

/**
 * Capture the viewport with `element` outlined, as `<stepKey>.png`. Resolves to null on
 * any failure or when even a 1× image is over the limit — the step then has no picture
 * rather than failing the recording. Never throws.
 */
export function captureStep(
  element: Element | null,
  stepKey: string,
  before?: FieldBefore
): Promise<File | null> {
  // Each queued capture holds a whole copy of the page until it renders. An author
  // racing ahead gets steps without pictures rather than a growing pile of copies.
  if (pending >= MAX_PENDING) return Promise.resolve(null);
  // Synchronous, so the picture is the page as it was when the author acted.
  let frame: HTMLElement | null = null;
  try {
    frame = freezeFrame(element, before);
  } catch {
    frame = null;
  }
  if (!frame) return Promise.resolve(null);
  pending += 1;
  const run = async (): Promise<File | null> => {
    if (!frame) return null;
    ensureHighlightStyle();
    document.body.appendChild(frame);
    try {
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      let blob = await renderFrame(frame, ratio);
      if (blob && blob.size > MAX_BYTES && ratio > 1) blob = await renderFrame(frame, 1);
      if (!blob || blob.size > MAX_BYTES) return null;
      return new File([blob], `${stepKey}.png`, { type: 'image/png' });
    } catch {
      return null;
    } finally {
      frame.remove();
      frame = null;
      pending -= 1;
    }
  };
  const next = queue.then(run, run);
  queue = next.catch(() => undefined);
  return next;
}
